/*
campaign.js

Behavior for campaign landing pages (layout/theme.campaign.liquid): hero video controls,
autoplaying clips, tap-to-play posts, swipe rows, lazy third-party embeds and click tracking.
No dependencies.
Initializers only run once per element, so the theme editor can re-run them on section reloads.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

  const once = (key, fn) => (el) => {
    if (el.dataset[key]) return;
    el.dataset[key] = 'true';
    fn(el);
  };

  /* Only one video plays with sound at a time. */
  const pauseOthers = (current) => {
    document.querySelectorAll('.cw-main video').forEach((video) => {
      if (video !== current && !video.paused && !video.muted) video.pause();
    });
  };

  document.addEventListener(
    'play',
    (event) => {
      if (event.target instanceof HTMLVideoElement && !event.target.muted) pauseOthers(event.target);
    },
    true
  );

  /* Click tracking: any [data-cw-track] element pushes a campaign_click event to GTM's dataLayer. */
  const campaign = document.body.dataset.cwCampaign || '';

  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-cw-track]');
    if (!el) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'campaign_click',
      campaign,
      campaign_target: el.dataset.cwTrack,
      campaign_url: el.href || undefined,
    });
  });

  /* Hero video: sound toggle, play/pause, progress bar, and pausing while scrolled away. */
  const initHero = (hero) => {
    const video = hero.querySelector('[data-cw-hero-video]');
    if (!video) return;

    const soundButton = hero.querySelector('[data-cw-sound]');
    const toggleButton = hero.querySelector('[data-cw-toggle]');
    const bar = hero.querySelector('[data-cw-progress]');
    const restartOnSound = hero.dataset.restartOnSound === 'true';
    let userPaused = false;
    let pausedOffscreen = false;
    let restarted = false;
    let frame = null;

    const syncToggle = () => {
      if (!toggleButton) return;
      toggleButton.classList.toggle('is-paused', video.paused);
      toggleButton.setAttribute(
        'aria-label',
        video.paused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause
      );
    };

    const syncSound = () => {
      if (soundButton) soundButton.setAttribute('aria-pressed', String(!video.muted));
    };

    const tick = () => {
      if (bar && video.duration) bar.style.transform = `scaleX(${video.currentTime / video.duration})`;
      frame = video.paused ? null : requestAnimationFrame(tick);
    };

    // play() is refused when autoplay is blocked (e.g. iOS Low Power Mode); the play button stays up.
    const play = () => video.play().catch(syncToggle);

    video.addEventListener('play', () => {
      syncToggle();
      if (!frame) frame = requestAnimationFrame(tick);
    });
    video.addEventListener('pause', syncToggle);
    video.addEventListener('volumechange', syncSound);

    if (soundButton) {
      soundButton.addEventListener('click', () => {
        if (video.muted) {
          video.muted = false;
          if (restartOnSound && !restarted) {
            video.currentTime = 0;
            restarted = true;
          }
          userPaused = false;
          pauseOthers(video);
          play();
        } else {
          video.muted = true;
        }
        syncSound();
      });
    }

    if (toggleButton) {
      toggleButton.addEventListener('click', () => {
        if (video.paused) {
          userPaused = false;
          play();
        } else {
          userPaused = true;
          video.pause();
        }
      });
    }

    new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (!video.paused) {
            pausedOffscreen = true;
            video.pause();
          }
        } else if (pausedOffscreen && !userPaused) {
          pausedOffscreen = false;
          play();
        }
      },
      { threshold: 0.15 }
    ).observe(video);

    if (reducedMotion.matches) {
      userPaused = true;
      video.pause();
    } else if (video.paused) {
      play();
    }

    syncToggle();
    syncSound();
  };

  /* Tap-to-play cards (clips, UGC posts). Nothing downloads until the first tap. */
  const initTapVideo = (wrap) => {
    const video = wrap.querySelector('video');
    const button = wrap.querySelector('.cw-tapvideo__btn');
    if (!video || !button) return;

    const sync = () => {
      const playing = !video.paused && !video.ended;
      wrap.classList.toggle('is-playing', playing);
      button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
    };

    button.addEventListener('click', () => {
      if (video.paused) {
        wrap.classList.add('is-active');
        video.play().catch(sync);
      } else {
        video.pause();
      }
    });

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('ended', () => {
      video.currentTime = 0;
      wrap.classList.remove('is-active');
      sync();
    });

    new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && !video.paused) video.pause();
    }).observe(wrap);
  };

  /* Muted looping clips with no controls: play while on screen and pause when scrolled away, so
     nothing downloads until a clip is near. Reduced-motion visitors see the poster. */
  const initAutoplayVideo = (video) => {
    if (reducedMotion.matches) return;
    video.muted = true;

    new IntersectionObserver(
      ([entry]) => {
        // play() is refused when autoplay is blocked (e.g. iOS Low Power Mode); the poster stays up.
        if (entry.isIntersecting) video.play().catch(() => {});
        else if (!video.paused) video.pause();
      },
      { rootMargin: '200px 0px' }
    ).observe(video);
  };

  /* Swipe rows: desktop arrows, carousel dots, and editor block selection. */
  const scrollPadding = (scroller) => parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0;

  const scrollToSlide = (scroller, slide) => {
    scroller.scrollTo({ left: slide.offsetLeft - scrollPadding(scroller), behavior: scrollBehavior() });
  };

  const initScroller = (wrap) => {
    const scroller = wrap.querySelector('[data-cw-scroller]');
    if (!scroller) return;

    const prev = wrap.querySelector('[data-cw-prev]');
    const next = wrap.querySelector('[data-cw-next]');
    const dotsEl = wrap.querySelector('[data-cw-dots]');
    const slides = Array.from(scroller.querySelectorAll(':scope > [data-cw-slide]'));
    let dots = [];

    if (dotsEl && slides.length > 1) {
      dotsEl.replaceChildren();
      dots = slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cw-dot';
        dot.setAttribute('aria-label', `Go to post ${index + 1} of ${slides.length}`);
        dot.addEventListener('click', () => scrollToSlide(scroller, slide));
        dotsEl.append(dot);
        return dot;
      });
    }

    const update = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      const overflows = max > 4;
      if (prev) prev.hidden = !overflows || scroller.scrollLeft <= 4;
      if (next) next.hidden = !overflows || scroller.scrollLeft >= max - 4;
      if (dotsEl) dotsEl.hidden = !overflows;
      if (!dots.length) return;

      let current = slides.length - 1;
      if (scroller.scrollLeft < max - 4) {
        const left = scroller.scrollLeft + scrollPadding(scroller);
        let closest = Infinity;
        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - left);
          if (distance < closest) {
            closest = distance;
            current = index;
          }
        });
      }
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
    };

    const page = (direction) => {
      scroller.scrollBy({ left: direction * scroller.clientWidth * 0.8, behavior: scrollBehavior() });
    };

    if (prev) prev.addEventListener('click', () => page(-1));
    if (next) next.addEventListener('click', () => page(1));

    let queued = false;
    scroller.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          update();
        });
      },
      { passive: true }
    );

    new ResizeObserver(update).observe(scroller);
  };

  /*
    Lazy third-party embeds (store locator, UGC feed). The markup sits in a <template> and is
    added when the section nears the viewport. Scripts parsed inside a <template> never run, so
    each one is swapped for a fresh <script> before insertion.
  */
  const loadEmbed = (holder) => {
    const template = holder.querySelector(':scope > template');
    if (!template || holder.dataset.cwLoaded) return;
    holder.dataset.cwLoaded = 'true';

    const fragment = document.importNode(template.content, true);
    fragment.querySelectorAll('script').forEach((inert) => {
      const script = document.createElement('script');
      Array.from(inert.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
      script.textContent = inert.textContent;
      inert.replaceWith(script);
    });

    const loading = holder.querySelector('[data-cw-embed-loading]');
    if (loading) loading.remove();
    holder.append(fragment);
  };

  const initLazyEmbed = (holder) => {
    if (holder.hasAttribute('data-cw-eager') || (window.Shopify && window.Shopify.designMode)) {
      loadEmbed(holder);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        loadEmbed(holder);
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(holder);
  };

  // A "Find it near you" tap (or a QR code linking to #find-it-near-you) loads the locator at once.
  const loadEmbedsForHash = (hash) => {
    if (!hash || hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.querySelectorAll('[data-cw-lazy-embed]').forEach(loadEmbed);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (link) loadEmbedsForHash(link.getAttribute('href'));
  });

  /* Setup */
  const init = (root) => {
    root.querySelectorAll('[data-cw-hero]').forEach(once('cwHeroReady', initHero));
    root.querySelectorAll('[data-cw-tapvideo]').forEach(once('cwTapReady', initTapVideo));
    root.querySelectorAll('[data-cw-autoplay]').forEach(once('cwAutoplayReady', initAutoplayVideo));
    root.querySelectorAll('[data-cw-scroller-wrap]').forEach(once('cwScrollerReady', initScroller));
    root.querySelectorAll('[data-cw-lazy-embed]').forEach(once('cwEmbedReady', initLazyEmbed));
  };

  init(document);
  loadEmbedsForHash(window.location.hash);

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target;
    const scroller = slide.parentElement;
    if (scroller && scroller.matches('[data-cw-scroller]')) scrollToSlide(scroller, slide);
  });
})();
